import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck, Check, X } from "lucide-react";

import MockBadge from "@/components/MockBadge";
import StatusPill from "@/components/StatusPill";
import { ops } from "@/lib/api";
import { fmtTime, shortId } from "@/lib/format";

export default function Compliance() {
  const qc = useQueryClient();
  // Live polling (react-query, 5s) — the human-review queue moves fast.
  const { data: queue = [] } = useQuery({
    queryKey: ["compliance-queue"],
    queryFn: ops.complianceQueue,
    refetchInterval: 5000,
    retry: false,
  });

  const decide = async (id, decision) => {
    try {
      await ops.complianceDecision(id, decision);
      toast.success(`Marked ${decision}`);
      qc.invalidateQueries({ queryKey: ["compliance-queue"] });
      qc.invalidateQueries({ queryKey: ["ops-queue"] });
      qc.invalidateQueries({ queryKey: ["ops-queue-badge"] });
    } catch (e) {
      toast.error("Failed to record decision");
    }
  };

  return (
    <div data-testid="page-compliance" className="p-6 md:p-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div className="min-w-0">
          <div className="pj-label mb-2">· GUARDIAN + SENTINEL · Human-in-the-loop</div>
          <h1 className="font-display text-[32px] md:text-[40px] leading-[1.02] truncate">Compliance Queue</h1>
        </div>
        <div className="flex-shrink-0">
          <MockBadge />
        </div>
      </div>

      <div className="pj-card">
        <div className="flex items-center justify-between p-4" style={{ borderBottom: "1px solid rgb(var(--ln))" }}>
          <h2 className="font-display text-[18px] flex items-center gap-2">
            <ShieldCheck size={14} className="text-[rgb(var(--dim))]" /> Pending review
          </h2>
          <span className="mono text-[10px] uppercase tracking-[0.14em] text-[rgb(var(--dim))]">
            {queue.length} items
          </span>
        </div>

        {queue.length === 0 && (
          <div className="p-10 mono text-[12px] text-[rgb(var(--dim))] text-center">
            Nothing pending. All checks have passed.
          </div>
        )}

        <div className="divide-y divide-[rgb(var(--ln))]">
          {queue.map((c) => (
            <div key={c.id} className="p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3" data-testid={`compliance-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="mono text-[10px] uppercase tracking-[0.14em] text-[rgb(var(--dim))] truncate max-w-full">{c.check_type}</span>
                  <StatusPill state={c.result} />
                  {c.severity && (
                    <span className="mono text-[10px] uppercase tracking-[0.14em]" style={{ color: c.severity === "high" ? "rgb(196,52,44)" : "rgb(176,116,18)" }}>
                      · {c.severity}
                    </span>
                  )}
                </div>
                <div className="mono text-[12px] mt-1 break-words">
                  {c.entity_type} · {shortId(c.entity_id)} · {fmtTime(c.created_at)}
                </div>
                <pre className="mono text-[10px] text-[rgb(var(--dim))] mt-2 max-h-32 overflow-auto whitespace-pre-wrap">
                  {JSON.stringify(c.detail, null, 2)}
                </pre>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 sm:ml-4">
                <button
                  type="button"
                  onClick={() => decide(c.id, "approve")}
                  data-testid={`btn-approve-${c.id}`}
                  className="mono inline-flex items-center gap-1 px-2 py-1.5 text-[10px] uppercase tracking-[0.12em]"
                  style={{ background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))", color: "rgb(252,249,243)" }}
                >
                  <Check size={11} /> Approve
                </button>
                <button
                  type="button"
                  onClick={() => decide(c.id, "reject")}
                  data-testid={`btn-reject-${c.id}`}
                  className="mono inline-flex items-center gap-1 px-2 py-1.5 text-[10px] uppercase tracking-[0.12em] border border-[rgb(var(--border-strong))] text-[rgb(var(--fg))] hover:border-[rgb(196,52,44)] hover:text-[rgb(196,52,44)]"
                >
                  <X size={11} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
